import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useMe } from '../auth/AuthContext';
import { AccountDialog } from '../account/AccountDialog';
import { applyTheme, loadTheme, type ThemePref } from '../lib/theme';
import { IconAuto, IconBell, IconLogo, IconMoon, IconSun, MODULE_ICONS } from '../ui/icons';
import { MODS, NAV_GROUPS } from './modules';
import { NotificationsDialog, useNavBadges, useNotificationEffects, useNotifications } from './Notifications';

const THEMES: { key: ThemePref; label: string; title: string; icon: () => JSX.Element }[] = [
  { key: 'light', label: 'Sáng', title: 'Giao diện sáng', icon: IconSun },
  { key: 'dark', label: 'Tối', title: 'Giao diện tối', icon: IconMoon },
  { key: 'auto', label: 'Tự động', title: 'Theo hệ thống', icon: IconAuto },
];

export function Shell() {
  const { can, logout } = useAuth();
  const me = useMe();
  const nav = useNavigate();
  const here = useLocation().pathname.split('/')[1] || '';
  const [theme, setTheme] = useState<ThemePref>(loadTheme);
  const [account, setAccount] = useState(false);
  const [notes, setNotes] = useState(false);
  const { unread } = useNotifications();
  const badges = useNavBadges();
  useNotificationEffects();

  const item = (m: string) => {
    const Icon = MODULE_ICONS[m];
    const n = m === 'tasks' ? badges.mine : m === 'cal' ? badges.today : 0;
    const tip = m === 'tasks' ? `${n} việc bạn đang phụ trách` : `${n} lịch hôm nay`;
    return (
      <button key={m} type="button" onClick={() => nav('/' + m)} aria-current={here === m ? 'page' : undefined}>
        <Icon /><span>{MODS[m].title}</span>{n > 0 && <span className="nt-n soft" title={tip}>{n}</span>}
      </button>
    );
  };

  return (
    <div className="shell">
      <aside className="side">
        <div className="brand">
          <div className="mark" aria-hidden="true"><IconLogo /></div>
          <div><h1>Cổng nghiệp vụ<br />Kế toán</h1><small>Kế toán – Tài chính</small></div>
        </div>

        <nav className="mods" aria-label="Chung">
          <button type="button" className="nt-btn" onClick={() => setNotes(true)}>
            <IconBell /><span>Thông báo</span>{unread.length > 0 && <span className="nt-n" aria-label={`${unread.length} thông báo chưa đọc`}>{unread.length > 99 ? '99+' : unread.length}</span>}
          </button>
          {['tasks', 'cal'].filter(can).map(item)}
        </nav>

        {NAV_GROUPS.map(g => {
          const mods = g.mods.filter(can);
          return mods.length ? (
            <div key={g.label} style={{ display: 'contents' }}>
              <div className="nav-l">{g.label}</div>
              <nav className="mods" aria-label={g.label}>{mods.map(item)}</nav>
            </div>
          ) : null;
        })}

        <div className="side-foot">
          <div className="theme-sw" role="radiogroup" aria-label="Chế độ xem">
            {THEMES.map(t => (
              <button key={t.key} type="button" role="radio" aria-checked={theme === t.key} title={t.title} onClick={() => { setTheme(t.key); applyTheme(t.key); }}>
                <t.icon /><span>{t.label}</span>
              </button>
            ))}
          </div>
          <div className="me">
            <div><b>{me.fullName || me.username}</b><small>{(me.role === 'admin' ? 'Quản trị' : 'Người dùng') + ' · ' + me.username}</small></div>
            <div className="me-act">
              <button type="button" className="btn small" onClick={() => setAccount(true)}>Tài khoản của tôi</button>
              <button type="button" className="btn small" onClick={() => void logout()}>Đăng xuất</button>
            </div>
          </div>
          <div className="status"><span className="dot on" /><span>Đã kết nối dữ liệu dùng chung</span></div>
        </div>
      </aside>

      <main className="app"><Outlet /></main>
      <AccountDialog open={account} onClose={() => setAccount(false)} />
      <NotificationsDialog open={notes} onClose={() => setNotes(false)} />
    </div>
  );
}
